import { Maximize2, XIcon } from "lucide-react";
import { useStore } from "zustand";
import { ChannelView } from "@/components/app/channel-view";
import { GridLink } from "@/components/app/grid";
import { ThreadCreationPanel } from "@/components/app/thread-creation-panel";
import { ThreadsPopover } from "@/components/app/threads-popover";
import { Button } from "@/components/ui/button";
import {
	ResizableHandle,
	ResizablePanel,
	ResizablePanelGroup,
} from "@/components/ui/resizable";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";

export function TextChannelView({ channelId }: { channelId: string }) {
	const channels = useStore(coreStore, (state) => state.channels);
	const setChannelViewOption = useStore(
		uiStore,
		(state) => state.setChannelViewOption
	);

	const channelViewOption = useStore(uiStore, (state) =>
		state.channelViewOptions.get(channelId)
	);

	const selectedChannel = channels.get(channelId);

	const openThread =
		channelViewOption?.sidePanel?.type === "thread"
			? channels.get(channelViewOption.sidePanel.threadId)
			: undefined;

	if (!selectedChannel) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-muted-foreground">Channel not found</p>
			</div>
		);
	}

	return (
		<div className="flex h-full flex-col">
			<ResizablePanelGroup className="flex-1" direction="horizontal">
				<ResizablePanel className="flex flex-col">
					<ChannelView.Root channelId={channelId}>
						<ChannelView.Header>
							<ChannelView.HeaderTitle>
								{selectedChannel.isThread && (
									<span className="mr-2 rounded bg-muted px-1.5 py-0.5 font-medium text-muted-foreground text-xs">
										Thread
									</span>
								)}
								{selectedChannel.name}
							</ChannelView.HeaderTitle>
							<ChannelView.HeaderGroup>
								<ChannelView.PeoplePopover />
								{!channelViewOption?.sidePanel && (
									<ThreadsPopover channelId={channelId} />
								)}
							</ChannelView.HeaderGroup>
						</ChannelView.Header>
						<ChannelView.MessageList />
						<ChannelView.MessageInput />
					</ChannelView.Root>
				</ResizablePanel>
				{channelViewOption?.sidePanel && (
					<>
						<ResizableHandle />
						<ResizablePanel className="flex flex-col" defaultSize={33}>
							{channelViewOption.sidePanel.type === "thread" && (
								<ChannelView.Root
									channelId={channelViewOption.sidePanel.threadId}
									mainChannelId={channelId}
								>
									<ChannelView.Header>
										<ChannelView.HeaderTitle>
											<span className="mr-2 rounded bg-muted px-1.5 py-0.5 font-medium text-muted-foreground text-xs">
												Thread
											</span>
											{openThread?.name}
										</ChannelView.HeaderTitle>
										<ChannelView.HeaderGroup>
											<Tooltip>
												<TooltipTrigger asChild>
													<Button asChild size="icon" variant="ghost">
														<GridLink
															to={{
																type: "thread",
																params: {
																	channelId,
																	threadId:
																		channelViewOption.sidePanel.threadId,
																},
															}}
														>
															<Maximize2 />
														</GridLink>
													</Button>
												</TooltipTrigger>
												<TooltipContent>Open in full view</TooltipContent>
											</Tooltip>
											<Tooltip>
												<TooltipTrigger asChild>
													<Button
														onClick={() =>
															setChannelViewOption(channelId, (prev) => ({
																...prev,
																sidePanel: undefined,
															}))
														}
														size="icon"
														variant="ghost"
													>
														<XIcon />
													</Button>
												</TooltipTrigger>
												<TooltipContent>Close thread</TooltipContent>
											</Tooltip>
										</ChannelView.HeaderGroup>
									</ChannelView.Header>
									<ChannelView.MessageList />
									<ChannelView.MessageInput />
								</ChannelView.Root>
							)}
							{channelViewOption.sidePanel.type === "create-thread" && (
								<ThreadCreationPanel
									channelId={channelId}
									replyToMessageId={
										channelViewOption.sidePanel.replyToMessageId
									}
								/>
							)}
						</ResizablePanel>
					</>
				)}
			</ResizablePanelGroup>
		</div>
	);
}
